import { useMemo } from 'react'

import type { FilterModel } from '@/components/lemmala-design-system/table/types'

import type { ViewManagerConfig } from './use-ag-view-manager'

/**
 * Compute per-view counters from rowData and filter models.
 * Requires an evaluator function that determines if a row matches a given FilterModel.
 */
export function useAgViewCounters<TRow>(
  rowData: TRow[],
  viewOptions: string[],
  getViewDefinition: ViewManagerConfig['getViewDefinition'],
  evaluate: (row: TRow, model?: FilterModel) => boolean,
  defaultCounters?: Record<string, number>,
) {
  return useMemo(() => {
    // If default counters provided, use them instead of calculating
    if (defaultCounters) {
      return defaultCounters
    }

    // Otherwise calculate from rowData
    const result: Record<string, number> = {}
    for (const name of viewOptions || []) {
      const def = getViewDefinition(name)
      const count = rowData.reduce(
        (acc, row) => (evaluate(row, (def?.filterModel as FilterModel) || {}) ? acc + 1 : acc),
        0,
      )
      result[name] = count
    }
    return result
  }, [rowData, viewOptions, getViewDefinition, evaluate, defaultCounters])
}
